import {
  ADDITION_CITADEL_ASSETS,
  ADDITION_CITADEL_ASSET_LIST,
} from './addition-citadel/AdditionCitadelAssets.js';
import { SUBTRACTION_KEEP_ASSET_LIST } from './subtraction-keep/SubtractionKeepAssets.js';
import { MEASUREMENT_MARKET_ASSET_LIST } from './measurement-market/MeasurementMarketAssets.js';
import { CLOCKTOWER_PLAZA_ASSET_LIST } from './clocktower-plaza/ClocktowerPlazaAssets.js';
import { COINMAKERS_VAULT_ASSET_LIST } from './coinmakers-vault/CoinmakersVaultAssets.js';
import { ROYAL_CHALLENGE_HALL_ASSET_LIST } from './royal-challenge-hall/RoyalChallengeHallAssets.js';

export const NUMBER_KINGDOM_DISTRICTS = {
  arrivalValley: 'arrival-valley',
  starBridge: 'star-bridge',
  kingdomCrossroads: 'kingdom-crossroads',
  hundredsCastle: 'hundreds-castle',
  additionCitadel: 'addition-citadel',
  subtractionCaverns: 'subtraction-caverns',
  problemSolvingVillage: 'problem-solving-village',
  royalMarket: 'royal-market',
  clockTower: 'clock-tower',
  measureworks: 'measureworks',
  geometryGardens: 'geometry-gardens',
  fractionIsles: 'fraction-isles',
  dataHarbor: 'data-harbor',
  masteryKeep: 'mastery-keep',
} as const;

export type NumberKingdomDistrict =
  typeof NUMBER_KINGDOM_DISTRICTS[keyof typeof NUMBER_KINGDOM_DISTRICTS];

export type NumberKingdomCurriculumDomain =
  | 'number-and-operations-base-ten'
  | 'operations-and-algebraic-thinking'
  | 'measurement-and-data'
  | 'geometry'
  | 'mixed-review';

export type DistrictImplementationStatus = 'playable' | 'asset-gated' | 'planned';

export interface DistrictArtAsset {
  readonly key: string;
  readonly path: string;
  readonly kind: 'image' | 'audio';
  readonly required: boolean;
}

export type DistrictUnlockRequirement =
  | { readonly kind: 'star-bridge-restored' }
  | { readonly kind: 'district-complete'; readonly districtId: NumberKingdomDistrict };

export interface NumberKingdomDistrictDefinition {
  readonly id: NumberKingdomDistrict;
  readonly displayName: string;
  readonly sceneKey?: string;
  readonly unlockRequirements: readonly DistrictUnlockRequirement[];
  readonly curriculumDomains: readonly NumberKingdomCurriculumDomain[];
  readonly completionStateKey: NumberKingdomDistrict;
  readonly route: string;
  readonly artManifest: readonly DistrictArtAsset[];
  readonly musicKey?: string;
  readonly ambienceKey?: string;
  readonly implementation: DistrictImplementationStatus;
}

const planned = (
  id: NumberKingdomDistrict,
  displayName: string,
  curriculumDomains: readonly NumberKingdomCurriculumDomain[],
): NumberKingdomDistrictDefinition => ({
  id,
  displayName,
  unlockRequirements: [],
  curriculumDomains,
  completionStateKey: id,
  route: `/number-kingdom/${id}`,
  artManifest: [],
  implementation: 'planned',
});

export const NUMBER_KINGDOM_DISTRICT_REGISTRY: Readonly<
  Record<NumberKingdomDistrict, NumberKingdomDistrictDefinition>
> = {
  [NUMBER_KINGDOM_DISTRICTS.arrivalValley]: {
    id: NUMBER_KINGDOM_DISTRICTS.arrivalValley,
    displayName: 'Arrival Valley',
    sceneKey: 'NumberKingdom',
    unlockRequirements: [],
    curriculumDomains: ['number-and-operations-base-ten'],
    completionStateKey: NUMBER_KINGDOM_DISTRICTS.arrivalValley,
    route: '/number-kingdom/arrival-valley',
    artManifest: [],
    implementation: 'playable',
  },
  [NUMBER_KINGDOM_DISTRICTS.starBridge]: {
    id: NUMBER_KINGDOM_DISTRICTS.starBridge,
    displayName: 'Star Bridge',
    sceneKey: 'NumberKingdom',
    unlockRequirements: [],
    curriculumDomains: [
      'number-and-operations-base-ten',
      'operations-and-algebraic-thinking',
    ],
    completionStateKey: NUMBER_KINGDOM_DISTRICTS.starBridge,
    route: '/number-kingdom/star-bridge',
    artManifest: [],
    implementation: 'playable',
  },
  [NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads]: {
    id: NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads,
    displayName: 'Kingdom Crossroads',
    sceneKey: 'KingdomCrossroads',
    unlockRequirements: [{ kind: 'star-bridge-restored' }],
    curriculumDomains: ['mixed-review'],
    completionStateKey: NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads,
    route: '/number-kingdom/kingdom-crossroads',
    artManifest: [ADDITION_CITADEL_ASSETS.kingdomCrossroads],
    implementation: 'playable',
  },
  [NUMBER_KINGDOM_DISTRICTS.hundredsCastle]: planned(
    NUMBER_KINGDOM_DISTRICTS.hundredsCastle,
    'Hundreds Castle',
    ['number-and-operations-base-ten'],
  ),
  [NUMBER_KINGDOM_DISTRICTS.additionCitadel]: {
    ...planned(
      NUMBER_KINGDOM_DISTRICTS.additionCitadel,
      'Addition Citadel',
      ['operations-and-algebraic-thinking'],
    ),
    unlockRequirements: [
      { kind: 'district-complete', districtId: NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads },
    ],
    sceneKey: 'AdditionCitadel',
    route: '/number-kingdom/addition-citadel',
    artManifest: ADDITION_CITADEL_ASSET_LIST,
    implementation: 'playable',
  },
  [NUMBER_KINGDOM_DISTRICTS.subtractionCaverns]: {
    ...planned(
      NUMBER_KINGDOM_DISTRICTS.subtractionCaverns,
      'Subtraction Keep',
      ['operations-and-algebraic-thinking'],
    ),
    sceneKey: 'SubtractionKeep',
    unlockRequirements: [
      { kind: 'district-complete', districtId: NUMBER_KINGDOM_DISTRICTS.additionCitadel },
    ],
    artManifest: SUBTRACTION_KEEP_ASSET_LIST,
    implementation: 'playable',
  },
  [NUMBER_KINGDOM_DISTRICTS.problemSolvingVillage]: planned(
    NUMBER_KINGDOM_DISTRICTS.problemSolvingVillage,
    'Problem-Solving Village',
    ['operations-and-algebraic-thinking'],
  ),
  [NUMBER_KINGDOM_DISTRICTS.royalMarket]: {
    ...planned(
      NUMBER_KINGDOM_DISTRICTS.royalMarket,
      "Coinmaker's Vault",
      ['measurement-and-data'],
    ),
    sceneKey: 'CoinmakersVault',
    unlockRequirements: [
      { kind: 'district-complete', districtId: NUMBER_KINGDOM_DISTRICTS.clockTower },
    ],
    artManifest: COINMAKERS_VAULT_ASSET_LIST,
    implementation: 'asset-gated',
  },
  [NUMBER_KINGDOM_DISTRICTS.clockTower]: {
    ...planned(
      NUMBER_KINGDOM_DISTRICTS.clockTower,
      'Clocktower Plaza',
      ['measurement-and-data'],
    ),
    sceneKey: 'ClocktowerPlaza',
    unlockRequirements: [
      { kind: 'district-complete', districtId: NUMBER_KINGDOM_DISTRICTS.measureworks },
    ],
    artManifest: CLOCKTOWER_PLAZA_ASSET_LIST,
    implementation: 'asset-gated',
  },
  [NUMBER_KINGDOM_DISTRICTS.measureworks]: {
    ...planned(
      NUMBER_KINGDOM_DISTRICTS.measureworks,
      'Measurement Market',
      ['measurement-and-data'],
    ),
    sceneKey: 'MeasurementMarket',
    unlockRequirements: [
      { kind: 'district-complete', districtId: NUMBER_KINGDOM_DISTRICTS.subtractionCaverns },
    ],
    artManifest: MEASUREMENT_MARKET_ASSET_LIST,
    implementation: 'asset-gated',
  },
  [NUMBER_KINGDOM_DISTRICTS.geometryGardens]: planned(
    NUMBER_KINGDOM_DISTRICTS.geometryGardens,
    'Geometry Gardens',
    ['geometry'],
  ),
  [NUMBER_KINGDOM_DISTRICTS.fractionIsles]: planned(
    NUMBER_KINGDOM_DISTRICTS.fractionIsles,
    'Fraction Isles',
    ['geometry'],
  ),
  [NUMBER_KINGDOM_DISTRICTS.dataHarbor]: planned(
    NUMBER_KINGDOM_DISTRICTS.dataHarbor,
    'Data Harbor',
    ['measurement-and-data'],
  ),
  [NUMBER_KINGDOM_DISTRICTS.masteryKeep]: {
    ...planned(
      NUMBER_KINGDOM_DISTRICTS.masteryKeep,
      'Royal Challenge Hall',
      ['mixed-review'],
    ),
    sceneKey: 'RoyalChallengeHall',
    unlockRequirements: [
      { kind: 'district-complete', districtId: NUMBER_KINGDOM_DISTRICTS.royalMarket },
    ],
    artManifest: ROYAL_CHALLENGE_HALL_ASSET_LIST,
    implementation: 'asset-gated',
  },
};

export const NUMBER_KINGDOM_DISTRICT_IDS = Object.values(NUMBER_KINGDOM_DISTRICTS);

export function isNumberKingdomDistrict(value: unknown): value is NumberKingdomDistrict {
  return typeof value === 'string'
    && NUMBER_KINGDOM_DISTRICT_IDS.includes(value as NumberKingdomDistrict);
}

export function getNumberKingdomDistrict(id: NumberKingdomDistrict) {
  return NUMBER_KINGDOM_DISTRICT_REGISTRY[id];
}
